// Sub pages. Each place opens a page built from its section of the text version in index.html, so the words live in one
// place. The title types in, the lead fades in word by word, and each block rises in after it.

const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

const page = document.getElementById('page') as HTMLElement;
const body = page.querySelector('.page-body') as HTMLElement;
const back = page.querySelector('.page-back') as HTMLButtonElement;

let timers: number[] = [];
let opener: HTMLElement | null = null;
let onClose: () => void = () => {};
export const isOpen = (): boolean => !page.hidden;

const later = (fn: () => void, ms: number): void => { timers.push(window.setTimeout(fn, ms)); };
const clear = (): void => { timers.forEach(clearTimeout); timers = []; };

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** Words as spans that fade in one after another. Returns when the last one starts. */
function words(target: HTMLElement, text: string, start: number, step: number): number {
  const parts = text.split(/\s+/).filter(Boolean);
  parts.forEach((wd, i) => {
    const s = el('span', 'w', wd + ' ');
    target.appendChild(s);
    if (reduce) s.classList.add('in');
    else later(() => s.classList.add('in'), start + i * step);
  });
  return start + parts.length * step;
}

function type(target: HTMLElement, text: string, step: number, done: () => void): void {
  if (reduce) { target.textContent = text; done(); return; }
  let i = 0;
  const tick = (): void => {
    target.textContent = text.slice(0, ++i);
    if (i < text.length) later(tick, step); else done();
  };
  tick();
}

/** One list item: an optional date, a bold title, then the rest of the sentence. */
function card(li: Element): HTMLElement {
  const c = el('li', 'card');
  const time = li.querySelector('time');
  const strong = li.querySelector('strong');
  if (time) c.appendChild(el('span', 'when', time.textContent ?? ''));
  if (strong) {
    c.appendChild(el('strong', undefined, strong.textContent ?? ''));
    const rest = (li.textContent ?? '').replace(time?.textContent ?? '', '').replace(strong.textContent ?? '', '').trim();
    if (rest) c.appendChild(el('span', 'text', rest));
  } else c.appendChild(el('span', 'text', li.textContent ?? ''));
  return c;
}

export interface Step { label: string; fn: () => void }

export function openPage(section: HTMLElement, from: HTMLElement | null, closed: () => void, pager?: { prev?: Step; next?: Step }): void {
  clear();
  onClose = closed;
  opener = from;
  body.textContent = '';
  const role = section.dataset.role ?? '';
  const title = section.querySelector('h2')?.textContent ?? '';
  const lead = section.querySelector('p')?.textContent ?? '';

  body.appendChild(el('p', 'eyebrow', role));
  const h1 = el('h1');
  h1.id = 'page-title';
  h1.setAttribute('aria-label', title);
  const typed = el('span');
  typed.setAttribute('aria-hidden', 'true');
  h1.appendChild(typed);
  h1.appendChild(el('span', 'caret'));
  body.appendChild(h1);
  const leadEl = el('p', 'lead');
  body.appendChild(leadEl);

  // the blocks under each h3
  const blocks: HTMLElement[] = [];
  let cur: HTMLElement | null = null;
  for (const node of Array.from(section.children)) {
    if (node.tagName === 'H3') {
      cur = el('section', 'block');
      cur.appendChild(el('h2', undefined, node.textContent ?? ''));
      blocks.push(cur);
    } else if (cur && node.tagName === 'P') {
      cur.appendChild(el('p', undefined, node.textContent ?? ''));
    } else if (cur && node.tagName === 'UL') {
      const ul = el('ul', 'cards');
      node.querySelectorAll('li').forEach((li) => ul.appendChild(card(li)));
      cur.appendChild(ul);
    }
  }
  if (pager && (pager.prev || pager.next)) { // the place before and the place after
    const nav = el('nav', 'block pager');
    nav.setAttribute('aria-label', 'Other places');
    const mk = (s: Step | undefined, cls: string, text: string): void => {
      if (!s) { nav.appendChild(el('span')); return; }
      const b = el('button', `step ${cls}`, text);
      b.type = 'button';
      b.addEventListener('click', s.fn);
      nav.appendChild(b);
    };
    mk(pager.prev, 'prev', pager.prev ? `\u2039 ${pager.prev.label}` : '');
    mk(pager.next, 'next', pager.next ? `${pager.next.label} \u203a` : '');
    blocks.push(nav);
  }
  blocks.forEach((b) => { b.classList.add('rise'); body.appendChild(b); });

  page.hidden = false;
  document.body.classList.add('paged');
  page.scrollTop = 0;
  back.focus({ preventScroll: true });

  type(typed, title, 38, () => {
    page.querySelector('.caret')?.classList.add('off');
    const after = words(leadEl, lead, 120, 34);
    blocks.forEach((b, i) => {
      if (reduce) b.classList.add('in');
      else later(() => b.classList.add('in'), after + 200 + i * 260);
    });
  });
}

export function closePage(): void {
  if (page.hidden) return;
  clear();
  page.hidden = true;
  document.body.classList.remove('paged');
  opener?.focus({ preventScroll: true });
  onClose();
}

back.addEventListener('click', closePage);
addEventListener('keydown', (e) => { if (e.key === 'Escape' && !page.hidden) { e.preventDefault(); closePage(); } });
